import type { RewardDetail } from "Infrastructure/Api/Api";
import { useCreateEditRewardForm } from "Infrastructure/Helpers/useCreateEditRewardForm";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { Box, Stack, useTheme } from "@mui/material";
import { SideDrawer } from "../../Drawers/SideDrawer";
import { Button } from "../Buttons/Button";
import { FormDatePickerInput } from "../Inputs/FormDatePickerInput";
import { FormNumberInput } from "../Inputs/FormNumberInput";
import { FormRichTextInput } from "../Inputs/FormRichTextInput";
import { FormTextInput } from "../Inputs/FormTextInput";

interface CreateRewardFormProps {
  mode?: "create" | "edit";
  reward?: RewardDetail | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateRewardForm = ({
  mode = "create",
  reward,
  open,
  onClose,
  onSuccess,
}: CreateRewardFormProps) => {
  const { colors, customSpacing, radius } = useTheme();
  const { t } = useResource();

  const {
    control,
    isPending,
    minExpirationDate,
    isStartDateInPast,
    handleFormSubmit,
    today,
    reset,
  } = useCreateEditRewardForm({
    mode,
    reward,
    open,
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  const handleClose = () => {
    if (!isPending) {
      reset();
      onClose();
    }
  };

  return (
    <SideDrawer
      open={open}
      onClose={handleClose}
      title={
        mode === "create"
          ? t(Resources.Pages.CreateReward.Title)
          : t(Resources.Pages.EditReward.Title)
      }
      disabled={isPending}
    >
      <Stack
        component="form"
        onSubmit={handleFormSubmit}
        sx={{
          flex: 1,
          minHeight: 0,
        }}
        spacing={customSpacing.xl}
      >
        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
          }}
          spacing={customSpacing.md}
        >
          <Stack
            sx={{
              backgroundColor: colors.background.primary,
              borderRadius: radius.xl,
              p: customSpacing.xl,
            }}
            spacing={customSpacing.xl}
          >
            <FormTextInput
              name="name"
              control={control}
              label={t(Resources.Pages.CreateReward.Fields.Title.Label)}
              required
              maxLength={50}
            />
            <Stack direction="row" gap={customSpacing.md}>
              <Box sx={{ flex: 1 }}>
                <FormNumberInput
                  name="cost"
                  control={control}
                  label={t(Resources.Pages.CreateReward.Fields.Cost.Label)}
                  required
                  suffix={t(Resources.Pages.CreateReward.Fields.Cost.Suffix)}
                  maxValue={1000000}
                  placeholder="0"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormNumberInput
                  name="quantity"
                  control={control}
                  label={t(Resources.Pages.CreateReward.Fields.LimitedQuantity)}
                  maxValue={10000}
                  placeholder="0"
                />
              </Box>
            </Stack>
            <Stack direction="row" gap={customSpacing.md}>
              <FormDatePickerInput
                name="startDate"
                control={control}
                label={t(Resources.Pages.CreateReward.Fields.Start)}
                disablePast={!isStartDateInPast}
                minDate={isStartDateInPast ? undefined : today}
                sx={{}}
              />
              <FormDatePickerInput
                name="endDate"
                control={control}
                label={t(Resources.Pages.CreateReward.Fields.TimeLimit)}
                minDate={minExpirationDate}
                sx={{}}
              />
            </Stack>
          </Stack>
          <Stack
            sx={{
              backgroundColor: colors.background.primary,
              borderRadius: radius.xl,
              p: customSpacing.xl,
              flex: 1,
              minHeight: 0,
            }}
          >
            <FormRichTextInput
              name="description"
              control={control}
              label={t(Resources.Pages.CreateReward.Fields.Description.Label)}
              maxLength={300}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            />
          </Stack>
        </Stack>
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
            flexShrink: 0,
            background: "transparent",
          }}
          spacing={customSpacing.md}
        >
          <Button
            type="tertiary"
            label={t(Resources.Common.Close)}
            onClick={handleClose}
            disabled={isPending}
          />
          <Button
            type="primary"
            label={
              mode === "create"
                ? t(Resources.Pages.CreateReward.SubmitText)
                : t(Resources.Pages.EditReward.SubmitText)
            }
            disabled={isPending}
            onClick={handleFormSubmit}
          />
        </Stack>
      </Stack>
    </SideDrawer>
  );
};
